import React, { createContext, useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../services/firebase";
import { expandFeatureList, resolvePlanFeatures } from "../constants/plans";
import { evaluateTenantLifecycle } from "../utils/tenantLifecycle";

const normalizePermissionsClaim = (permissionsClaim) => {
    if (Array.isArray(permissionsClaim)) {
        return permissionsClaim.reduce((acc, permissionKey) => {
            if (permissionKey) acc[permissionKey] = true;
            return acc;
        }, {});
    }

    if (permissionsClaim && typeof permissionsClaim === "object") {
        return permissionsClaim;
    }

    return {};
};

export const TenantContext = createContext({
    idTenant: null,
    tenantName: null,
    tenantSlug: null,
    tenantLogo: null,
    primaryBranch: [],
    planId: null,
    featureOverrides: {},
    effectiveFeatures: {},
    tenantLifecycle: null,
    claims: null,
    user: null,
    loading: true,
    hasNeoMethodology: false,
    methodologyNeoPrice: 149
});

const normalizeFeaturesClaim = (featuresClaim) => {
    return expandFeatureList(featuresClaim);
};

export const TenantProvider = ({ children }) => {
    const [idTenant, setIdTenant] = useState(null);
    const [tenantName, setTenantName] = useState(null);
    const [tenantSlug, setTenantSlug] = useState(null);
    const [tenantLogo, setTenantLogo] = useState(null);
    const [primaryBranch, setPrimaryBranch] = useState([]);
    const [planId, setPlanId] = useState(null);
    const [featureOverrides, setFeatureOverrides] = useState({});
    const [effectiveFeatures, setEffectiveFeatures] = useState({});
    const [tenantLifecycle, setTenantLifecycle] = useState(null);
    const [claims, setClaims] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasNeoMethodology, setHasNeoMethodology] = useState(false);
    const [methodologyNeoPrice, setMethodologyNeoPrice] = useState(149);

    useEffect(() => {
        // Observe auth state changes locally to re-fetch claims and profile
        // Alternatively, we could hook into AuthContext, but hooking into onAuthStateChanged
        // directly here is often more robust for Firebase timing on ID tokens.
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    let tokenResult = await firebaseUser.getIdTokenResult();
                    let customClaims = tokenResult.claims;

                    const normalizedPermissionsClaim = normalizePermissionsClaim(customClaims?.permissions);
                    const normalizedFeaturesClaim = normalizeFeaturesClaim(
                        customClaims?.features || customClaims?.effectiveFeatures || customClaims?.planFeatures
                    );
                    const isEmployeeWithoutPermissions = customClaims?.role === 'employee' && Object.keys(normalizedPermissionsClaim).length === 0;
                    const needsClaimsRefresh = customClaims?.role && (
                        !customClaims.planId
                        || Object.keys(normalizedFeaturesClaim).length === 0
                        || isEmployeeWithoutPermissions
                    );

                    if (needsClaimsRefresh) {
                        try {
                            const refreshMyClaims = httpsCallable(functions, "refreshMyClaims");
                            await refreshMyClaims();
                            tokenResult = await firebaseUser.getIdTokenResult(true);
                            customClaims = tokenResult.claims;
                        } catch (refreshError) {
                            console.warn("[TenantContext] Falha ao refrescar claims. Continuando com claims atuais.", refreshError);
                        }
                    }

                    const role = customClaims.role || null;
                    const tenant = customClaims.idTenant || null;
                    let permissions = normalizedPermissionsClaim;
                    const claimPlanId = customClaims.planId || customClaims.plan || null;
                    const claimEffectiveFeatures = normalizedFeaturesClaim;

                    // Buscando perfil global do usuário no Firestore
                    const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
                    const profileData = userDoc.exists() ? userDoc.data() : {};

                    // FALLBACK: Se employee ainda sem permissões após refresh, busca direto do cargo no Firestore
                    if (role === 'employee' && Object.keys(permissions).length === 0 && tenant) {
                        const idRole = customClaims.idRole || profileData.idRole;
                        if (idRole) {
                            try {
                                const roleDoc = await getDoc(doc(db, "tenants", tenant, "roles", idRole));
                                if (roleDoc.exists()) {
                                    const rolePermissions = roleDoc.data().permissions || {};
                                    permissions = normalizePermissionsClaim(rolePermissions);
                                }
                            } catch (roleError) {
                                console.warn("[TenantContext] Fallback de permissões via cargo falhou.", roleError);
                            }
                        }
                    }

                    let tName = null;
                    let tSlug = null;
                    let tLogo = null;
                    let tPrimaryBranch = [];
                    let tPlanId = claimPlanId;
                    let tFeatureOverrides = {};
                    let tEffectiveFeatures = claimEffectiveFeatures;
                    let tLifecycle = null;
                    let tHasNeoMethodology = false;
                    let tMethodologyNeoPrice = 149;
                    if (tenant) {
                        const tenantDoc = await getDoc(doc(db, "tenants", tenant));
                        if (tenantDoc.exists()) {
                            const tenantData = tenantDoc.data();
                            tName = tenantData.name || null;
                            tSlug = tenantData.slug || null;
                            tLogo = tenantData.logoUrl || null;
                            tPrimaryBranch = Array.isArray(tenantData.primaryBranch) ? tenantData.primaryBranch : [];
                            tPlanId = claimPlanId || tenantData.planId || tenantData.plan || "neo";
                            tFeatureOverrides = tenantData.featureOverrides || {};
                            tEffectiveFeatures = Object.keys(claimEffectiveFeatures).length > 0
                                ? claimEffectiveFeatures
                                : resolvePlanFeatures(
                                    tPlanId,
                                    tenantData.featureOverrides || {},
                                    tenantData.effectiveFeatures || tenantData.planFeatures || null
                                );
                            tLifecycle = evaluateTenantLifecycle(tenantData);
                            tHasNeoMethodology = tenantData.hasNeoMethodology === true;
                            const platformPlanId = tenantData.platformPlanId || tenantData.planId || tenantData.plan || null;
                            if (platformPlanId) {
                                try {
                                    const platformPlanDoc = await getDoc(doc(db, "platformPlans", platformPlanId));
                                    if (platformPlanDoc.exists()) {
                                        const platformPlanData = platformPlanDoc.data();
                                        tMethodologyNeoPrice = platformPlanData.methodologyNeoPrice !== undefined 
                                            ? platformPlanData.methodologyNeoPrice 
                                            : 149;
                                    }
                                } catch (err) {
                                    console.warn("[TenantContext] Erro ao carregar preco do plano:", err);
                                }
                            }
                        }

                    }

                    if (!tenant) {
                        tEffectiveFeatures = resolvePlanFeatures(
                            tPlanId || "neo",
                            {},
                            Object.keys(claimEffectiveFeatures).length > 0 ? claimEffectiveFeatures : null
                        );
                    }
                    const finalPermissions = permissions;

                    setIdTenant(tenant);
                    setTenantName(tName);
                    setTenantSlug(tSlug);
                    setTenantLogo(tLogo);
                    setPrimaryBranch(tPrimaryBranch);
                    setPlanId(tPlanId || "neo");
                    setFeatureOverrides(tFeatureOverrides);
                    setEffectiveFeatures(tEffectiveFeatures || {});
                    setTenantLifecycle(tLifecycle);
                    setHasNeoMethodology(tHasNeoMethodology);
                    setMethodologyNeoPrice(tMethodologyNeoPrice);
                    setClaims({
                        role,
                        idTenant: tenant,
                        permissions: finalPermissions,
                        planId: tPlanId || "neo",
                        planVersion: customClaims.planVersion || null,
                        features: Object.keys(tEffectiveFeatures || {}).filter((featureKey) => tEffectiveFeatures?.[featureKey] === true),
                        effectiveFeatures: tEffectiveFeatures || {}
                    });

                    const loggedUser = {
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        phone: profileData.phone || profileData.phoneNumber || null,
                        fullName: profileData.fullName || profileData.name || null,
                        displayName: profileData.fullName || profileData.name || firebaseUser.displayName || firebaseUser.email,
                        photoURL: profileData.photoUrl || profileData.photoURL || profileData.avatar || firebaseUser.photoURL,
                        role: role,
                        permissions: finalPermissions
                    };

                    setUser(loggedUser);


                } catch (error) {
                    console.error("Erro ao processar dados de Tenant:", error);
                }
            } else {
                setIdTenant(null);
                setTenantName(null);
                setTenantSlug(null);
                setTenantLogo(null);
                setPrimaryBranch([]);
                setPlanId(null);
                setFeatureOverrides({});
                setEffectiveFeatures({});
                setTenantLifecycle(null);
                setClaims(null);
                setUser(null);
                setHasNeoMethodology(false);
                setMethodologyNeoPrice(149);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const refreshTenant = async () => {
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
            try {
                const refreshMyClaims = httpsCallable(functions, "refreshMyClaims");
                await refreshMyClaims();
                await firebaseUser.getIdTokenResult(true);
                window.location.reload();
            } catch (err) {
                console.error("Error refreshing claims:", err);
            }
        }
    };

    const value = {
        idTenant,
        tenantName,
        tenantSlug,
        tenantLogo,
        primaryBranch,
        planId,
        featureOverrides,
        effectiveFeatures,
        tenantLifecycle,
        claims,
        user,
        loading,
        hasNeoMethodology,
        methodologyNeoPrice,
        refreshTenant
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};
